/**
 * Pure helpers for the client notes timeline: who wrote an entry, matching a
 * search, splitting a body into highlighted runs, and grouping by month.
 */
import type { ForeignNationalNote } from "@/api/foreignNationals";
import { ASSISTANT_PERSONA } from "@/features/agents/types";

/** The author filter: a person's uid, `assistant`, or everyone. */
export const EVERYONE = "all";
export const ASSISTANT_KEY = "assistant";

export const authorKey = (note: ForeignNationalNote): string =>
  note.source === "assistant"
    ? ASSISTANT_KEY
    : note.author_uid || note.author_name || "unknown";

/** Assistant notes are Casey's, whatever title was stored with them. */
export const authorName = (note: ForeignNationalNote): string =>
  note.source === "assistant"
    ? ASSISTANT_PERSONA
    : note.author_name || "Unknown";

export const firstName = (name: string): string =>
  name.trim().split(/\s+/)[0] ?? name;

export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
};

export const isEdited = (note: ForeignNationalNote): boolean =>
  Boolean(note.edited_at);

/** The body as plain text: no `**`, list items joined with " · ". */
export const plainText = (body: string): string =>
  body
    .replace(/\*\*/g, "")
    .replace(/\n\s*[-*]\s+/g, " · ")
    .replace(/\s*\n\s*/g, " ")
    .trim();

/** The lowercase words of a query. */
export const queryWords = (query: string): string[] =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

/** Every query word appears in the body, the author or the linked case. */
export const matchesQuery = (
  note: ForeignNationalNote,
  query: string,
  caseLabel?: string | null
): boolean => {
  const words = queryWords(query);
  if (!words.length) return true;
  const hay = [plainText(note.body), authorName(note), caseLabel ?? ""]
    .join("\n")
    .toLowerCase();
  return words.every((w) => hay.includes(w));
};

export type NoteFilters = {
  query: string;
  author: string;
  pinnedOnly: boolean;
};

export const filterNotes = (
  notes: ForeignNationalNote[],
  filters: NoteFilters,
  caseLabelOf: (caseId: string | null) => string | null = () => null
): ForeignNationalNote[] =>
  notes.filter(
    (n) =>
      (filters.author === EVERYONE || authorKey(n) === filters.author) &&
      (!filters.pinnedOnly || n.pinned) &&
      matchesQuery(n, filters.query, caseLabelOf(n.case_id))
  );

export const isNarrowed = (filters: NoteFilters): boolean =>
  Boolean(filters.query.trim()) ||
  filters.author !== EVERYONE ||
  filters.pinnedOnly;

export type Run = { text: string; hit: boolean };

/** `text` split around case-insensitive hits of any query word. */
export const highlightRuns = (text: string, query: string): Run[] => {
  const words = queryWords(query);
  if (!words.length || !text) return text ? [{ text, hit: false }] : [];
  const lower = text.toLowerCase();
  const marks = new Array<boolean>(text.length).fill(false);
  for (const w of words) {
    let from = lower.indexOf(w);
    while (from >= 0) {
      marks.fill(true, from, from + w.length);
      from = lower.indexOf(w, from + w.length);
    }
  }
  const runs: Run[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = runs[runs.length - 1];
    if (last && last.hit === marks[i]) last.text += text[i];
    else runs.push({ text: text[i], hit: marks[i] });
  }
  return runs;
};

export type InlineRun = { text: string; bold: boolean };

/** `**bold**` spans of one line. */
export const inlineRuns = (line: string): InlineRun[] =>
  line
    .split("**")
    .map((text, i) => ({ text, bold: i % 2 === 1 }))
    .filter((r) => r.text);

export type BodyBlock = { list: boolean; items: string[] };

/** Paragraph lines and `- ` lists, the only markdown a note renders. */
export const bodyBlocks = (body: string): BodyBlock[] => {
  const blocks: BodyBlock[] = [];
  for (const raw of body.split("\n")) {
    const item = /^\s*[-*]\s+/.test(raw);
    const text = item ? raw.replace(/^\s*[-*]\s+/, "") : raw;
    const last = blocks[blocks.length - 1];
    if (item && last?.list) last.items.push(text);
    else if (item || text.trim()) blocks.push({ list: item, items: [text] });
  }
  return blocks;
};

const time = (iso: string | null): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

export const byNewest = (a: ForeignNationalNote, b: ForeignNationalNote) =>
  time(b.created_at) - time(a.created_at);

/** "October 2026". */
export const monthLabel = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric"
      })
    : "Undated";

/** "Oct 12, 2026 · 3:41 PM". */
export const noteDateLabel = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  const at = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
  return `${date} · ${at}`;
};

/** "Oct 12, 2026". */
export const shortDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    : "";

export type MonthGroup = {
  key: string;
  label: string;
  notes: ForeignNationalNote[];
};

/** Pinned entries first (newest first), then the rest by month, newest first. */
export const groupNotes = (
  notes: ForeignNationalNote[]
): { pinned: ForeignNationalNote[]; months: MonthGroup[] } => {
  const sorted = [...notes].sort(byNewest);
  const months: MonthGroup[] = [];
  for (const note of sorted.filter((n) => !n.pinned)) {
    const label = monthLabel(note.created_at);
    const last = months[months.length - 1];
    if (last?.label === label) last.notes.push(note);
    else months.push({ key: label, label, notes: [note] });
  }
  return { pinned: sorted.filter((n) => n.pinned), months };
};

export type AuthorChip = { key: string; label: string; count: number };

/** Everyone, then each author by how many entries they wrote. */
export const authorChips = (notes: ForeignNationalNote[]): AuthorChip[] => {
  const byKey = new Map<string, AuthorChip>();
  for (const note of notes) {
    const key = authorKey(note);
    const name = authorName(note);
    const chip = byKey.get(key) ?? {
      key,
      label: key === ASSISTANT_KEY ? name : firstName(name),
      count: 0
    };
    chip.count += 1;
    byKey.set(key, chip);
  }
  const people = [...byKey.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label)
  );
  return [{ key: EVERYONE, label: "Everyone", count: notes.length }, ...people];
};

/** The passage around the first hit, for a search row. */
export const snippetOf = (body: string, query: string, after = 64): string => {
  const plain = plainText(body);
  const words = queryWords(query);
  const lower = plain.toLowerCase();
  const hits = words.map((w) => lower.indexOf(w)).filter((i) => i >= 0);
  if (!hits.length) return plain.slice(0, 96) + (plain.length > 96 ? "…" : "");
  const at = Math.min(...hits);
  const start = Math.max(0, at - 28);
  const end = Math.min(plain.length, at + after + 28);
  return (
    (start > 0 ? "…" : "") +
    plain.slice(start, end) +
    (end < plain.length ? "…" : "")
  );
};
